/**
 * Dettaglio cliente CRM in MaeDesign: testata identità, indicatori reali e schede
 * (Anagrafica · Società collegate · Commesse · Fatturazione · CRM).
 *
 * Prima era una `Grid` fissa 5/7 con tutte le card impilate in un'unica pagina: su
 * portatile le due colonne si schiacciavano e la tabella dei collegamenti sforava.
 * Ora la testata va a capo, gli indicatori sono una `SimpleGrid` responsive e ogni
 * area è una scheda a sé, così nessuna sezione compete per lo spazio con le altre.
 *
 * Regola sui numeri: tutto ciò che è mostrato viene dal BE. Dove il dato non esiste
 * (fatturazione non popolata, nessuna commessa) la card lo dice — nessun placeholder
 * che somigli a un valore.
 */
import { useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Anchor, Button, Center, Group, Loader, SimpleGrid, Stack, Text, Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle, IconArrowLeft, IconBriefcase, IconBuilding, IconBuildingSkyscraper,
  IconEdit, IconFileInvoice, IconId, IconMailForward, IconReceipt2, IconTargetArrow, IconBuildingStore, IconUserEdit,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { MaePageHead, MaeShell, MaeTabPills } from '@/shared/desktop/MaeShell';
import { ExternalSyncNotice } from '@/features/admin/ExternalSyncNotice';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { formatDate, formatMoney } from '@/shared/lib/format';
import { ClientFormModal } from '@/features/clients/ClientFormModal';
import { ClientCrmSection } from '@/features/crm/ClientCrmSection';
import { CliCard, CliKpi } from '@/features/clients/ClientBits';
import { CLI_TONE } from '@/features/clients/ClientBits.utils';
import { ClientMasterDataCard, ClientContactsCard } from '@/features/clients/ClientIdentityCards';
import { statusUi } from '@/features/clients/ClientLinksCard.utils';
import { ClientLinksCard } from '@/features/clients/ClientLinksCard';
import { ClientVpsCard } from '@/features/clients/ClientVpsCard';
import { ChangeRequestsReview } from '@/features/client-change-requests/ChangeRequestsReview';
import { useAbility } from '@/app/providers/AbilityProvider';
import { ClientPortalAccountsCard } from '@/features/portal/admin/ClientPortalAccountsCard';
import { PortalVisibilityCard } from '@/features/portal/admin/PortalVisibilityCard';
import { ClientPortalDocumentsCard } from '@/features/portal/admin/ClientPortalDocumentsCard';
import { ClientBillingCards, ClientProjectsCard } from '@/features/clients/ClientCommercialCards';
import {
  useClientInvoices, useClientProjects, useClientSalesOrders,
} from '@/features/clients/ClientCommercialCards.utils';
import {
  clientDisplayName, clientInitials, dominantStatus, summarizeInvoices, summarizeProjects,
  type ClientRow, type TenantClientLink,
} from '@/features/clients/api';

export const Route = createFileRoute('/_app/clients/$clientId')({
  component: ClientDetailPage,
  /* `?tab=changes` apre le richieste di modifica: ci porta l'avviso «variazione anagrafica». */
  validateSearch: (s: Record<string, unknown>): { tab?: 'changes' } => (s.tab === 'changes' ? { tab: 'changes' } : {}),
});

type TabValue = 'anagrafica' | 'links' | 'projects' | 'billing' | 'crm' | 'portal' | 'vps' | 'changes';

const TABS = [
  { value: 'anagrafica', label: 'Anagrafica', icon: IconId },
  { value: 'links', label: 'Società collegate', icon: IconBuilding },
  { value: 'projects', label: 'Progetti', icon: IconBriefcase },
  { value: 'billing', label: 'Fatturazione', icon: IconFileInvoice },
  { value: 'crm', label: 'CRM', icon: IconTargetArrow },
  { value: 'portal', label: 'Portale', icon: IconBuildingStore },
  /* Sta dopo «Portale» e non dentro l'anagrafica: i destinatari del verbale sono una
     configurazione di invio, non un dato identificativo del cliente. */
  { value: 'vps', label: 'VPS', icon: IconMailForward },
  /* Le richieste di modifica dal portale (task 2056): solo per chi può modificare il cliente. */
  { value: 'changes', label: 'Richieste di modifica', icon: IconUserEdit },
] as const;

function ClientDetailPage() {
  const { clientId } = useParams({ from: '/_app/clients/$clientId' });
  const navigate = useNavigate();
  const isMobile = useIsMobileShell();
  const [editOpen, setEditOpen] = useState(false);
  const ability = useAbility();
  const canDecideChanges = ability.can('update', 'Client');
  const asked = Route.useSearch().tab;
  const [tab, setTab] = useState<TabValue>(asked === 'changes' && canDecideChanges ? 'changes' : 'anagrafica');
  const tabs = canDecideChanges ? TABS : TABS.filter((t) => t.value !== 'changes');

  const clientQ = useQuery({
    queryKey: ['client-detail', clientId],
    queryFn: () => fetchOne<ClientRow>('clients', clientId, { include: ['linkedTenant'] }),
  });

  // Collegamenti tenant-client: fetch unico condiviso da "Società collegate", dalle
  // commesse (filtrate per gli id dei collegamenti) e dal pannello CRM.
  const linksQ = useQuery({
    queryKey: ['client-tenant-links', clientId],
    queryFn: () => fetchList<TenantClientLink>('tenant-clients', {
      filter: { 'client.id': { eq: clientId } },
      include: ['tenant', 'leadOwner'],
      page: { size: 100 },
    }),
  });
  const links = linksQ.data?.data ?? [];

  // Indicatori e schede condividono queste query: sollevate qui così passare da una
  // scheda all'altra non rifà le chiamate.
  const projectsQ = useClientProjects(links);
  const invoicesQ = useClientInvoices(clientId);
  const ordersQ = useClientSalesOrders(clientId);

  if (clientQ.isLoading) {
    return (
      <MaeShell>
        <Center py={80}><Loader /></Center>
      </MaeShell>
    );
  }

  const client = clientQ.data;
  if (!client) {
    return (
      <MaeShell>
        <Stack gap={14} align="flex-start">
          <BackLink />
          <CliCard>
            <Group gap={10} wrap="nowrap">
              <IconAlertTriangle size={18} style={{ color: 'var(--warning)' }} />
              <Text fz="sm" c="var(--ink-700)">
                Cliente non trovato: potrebbe essere stato rimosso, oppure non è nel portafoglio della società attiva.
              </Text>
            </Group>
          </CliCard>
        </Stack>
      </MaeShell>
    );
  }

  const isPrivate = client.clientType === 'private';
  const projTotals = summarizeProjects(projectsQ.data?.data ?? []);
  const invTotals = summarizeInvoices(invoicesQ.data?.data ?? []);
  const billingUnavailable = invoicesQ.isError;

  const inner = (
    <>
      <MaePageHead
        onBack={() => void navigate({ to: '/clients' })}
        backLabel="Torna ai clienti"
        title={clientDisplayName(client)}
        sub={[
          client.vatNumber ? `P.IVA ${client.vatNumber}` : client.taxId ? `CF ${client.taxId}` : null,
          client.city,
          client.industryCode,
          client.createdAt ? `in anagrafica dal ${formatDate(client.createdAt)}` : null,
        ].filter(Boolean).join(' · ') || 'Nessun dato identificativo registrato'}
        icon={<span aria-hidden style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, letterSpacing: '-0.01em' }}>{clientInitials(client)}</span>}
        tone="azzurro"
        badges={<>
          <IdentityChip bg="var(--surface-2)" fg="var(--ink-500)">{isPrivate ? 'Privato' : 'Azienda'}</IdentityChip>
          {(() => {
            const dom = dominantStatus(links);
            if (!dom) return null;
            const ui = statusUi(dom);
            return (
              <Tooltip
                label={links.length > 1
                  ? 'Stato più avanzato tra le società collegate: le altre possono differire'
                  : 'Stato commerciale del cliente'}
                withArrow
              >
                <IdentityChip bg={ui.bg} fg={ui.fg}>{ui.label}</IdentityChip>
              </Tooltip>
            );
          })()}
          {client.linkedTenant && (
            <Tooltip label="Questo cliente è anche un’azienda della piattaforma (B2B reciproco)" withArrow>
              <IdentityChip bg="var(--info-bg)" fg="var(--accent-text)">
                <Group gap={4} wrap="nowrap"><IconBuildingSkyscraper size={11} />Azienda del gruppo</Group>
              </IdentityChip>
            </Tooltip>
          )}
        </>}
        actions={
          <Button
            h={isMobile ? 44 : 40}
            fullWidth={isMobile}
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() => setEditOpen(true)}
          >
            Modifica anagrafica
          </Button>
        }
      />

      {/* Su questa azienda `clients` risponde `read-only` nel registro delle sincronizzazioni:
          diciotto campi arrivano dal gestionale esterno. Dirlo qui evita che qualcuno compili
          una scheda che il salvataggio rifiuta con `EXTERNALLY_MANAGED`. */}
      <ExternalSyncNotice resourceType="clients" records={[client]} />

      {/* Quattro numeri brevi: a una colonna erano 340px di scorrimento prima delle schede.
          `base: 2` li tiene sopra la piega, ed è la stessa griglia della fatturazione. */}
      <SimpleGrid cols={{ base: 2, lg: 4 }} spacing={{ base: 10, sm: 16 }} mb={isMobile ? 16 : 20}>
        <CliKpi
          label="Società collegate"
          value={links.length}
          icon={IconBuilding}
          tone={CLI_TONE.client}
          loading={linksQ.isLoading}
          hint={links.length === 0 ? 'nessuna società lo ha in portafoglio' : 'del gruppo'}
        />
        <CliKpi
          label="Progetti aperti"
          value={links.length === 0 ? '—' : `${projTotals.open}/${projTotals.total}`}
          icon={IconBriefcase}
          tone={projTotals.open > 0 ? CLI_TONE.ok : CLI_TONE.muted}
          loading={projectsQ.isLoading}
          hint={projTotals.total > 0 ? `${projTotals.closed} chiuse` : undefined}
        />
        <CliKpi
          label="Fatturato"
          value={billingUnavailable ? '—' : formatMoney(invTotals.invoiced, invTotals.currency)}
          icon={IconReceipt2}
          tone={CLI_TONE.money}
          loading={invoicesQ.isLoading}
          hint={billingUnavailable ? 'fatturazione non disponibile' : `${invTotals.count} ${invTotals.count === 1 ? 'fattura' : 'fatture'}`}
        />
        <CliKpi
          label="Da incassare"
          value={billingUnavailable ? '—' : formatMoney(invTotals.outstanding, invTotals.currency)}
          icon={IconFileInvoice}
          tone={invTotals.overdue > 0 ? CLI_TONE.bad : invTotals.outstanding > 0 ? CLI_TONE.warn : CLI_TONE.muted}
          loading={invoicesQ.isLoading}
          hint={invTotals.overdue > 0 ? `${formatMoney(invTotals.overdue, invTotals.currency)} scaduti` : undefined}
        />
      </SimpleGrid>

      <MaeTabPills tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'anagrafica' && (
        <Stack gap={16}>
          <ClientMasterDataCard client={client} />
          <ClientContactsCard clientId={clientId} />
        </Stack>
      )}
      {tab === 'links' && <ClientLinksCard clientId={clientId} links={links} loading={linksQ.isLoading} />}
      {tab === 'projects' && <ClientProjectsCard links={links} query={projectsQ} />}
      {tab === 'billing' && <ClientBillingCards invoicesQ={invoicesQ} ordersQ={ordersQ} />}
      {tab === 'crm' && <ClientCrmSection clientId={clientId} links={links} />}
      {tab === 'vps' && <ClientVpsCard client={client} />}
      {tab === 'changes' && <ChangeRequestsReview clientId={clientId} />}
      {tab === 'portal' && (
        <Stack gap={16}>
          <ClientPortalAccountsCard clientId={clientId} />
          <PortalVisibilityCard clientId={clientId} />
          <ClientPortalDocumentsCard clientId={clientId} />
        </Stack>
      )}

      <ClientFormModal opened={editOpen} onClose={() => setEditOpen(false)} client={client} />
    </>
  );

  if (isMobile) return <div style={{ padding: '16px 16px 28px' }}>{inner}</div>;
  return <MaeShell>{inner}</MaeShell>;
}

function BackLink() {
  return (
    <Anchor component={Link} to="/clients" fz="xs" c="var(--ink-500)" underline="hover">
      <Group gap={5} wrap="nowrap"><IconArrowLeft size={14} /> Torna ai clienti</Group>
    </Anchor>
  );
}

function IdentityChip({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '4px 10px', borderRadius: 999,
      background: bg, color: fg, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}
